import { Fragment, createContext, createElement, useContext, useEffect, useState } from "react";
import { BACKEND } from "@/lib/host";
import { REGISTRY } from "@/content/registry";

// Website copy: overrides from GET /api/content on top of built-in defaults (REGISTRY). Plain text only.
const CACHE_KEY = "samrtin.content.v1";
let overrides = (() => { try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || {}; } catch { return {}; } })();
const Ctx = createContext({ version: 0, maintenance: false });

export function tx(key, vars) {
  const r = REGISTRY[key];
  if (r?.kind === "list") {
    console.warn(`tx("${key}") is a list entry; use contentList() instead`);
    return r.label || "";
  }
  let s = overrides[key] ?? r?.default ?? key;
  if (vars) s = s.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
  return s;
}

// JSX text: keeps line breaks of multi-line entries without injecting HTML.
export function txt(key, vars) {
  const s = tx(key, vars);
  if (!s.includes("\n")) return s;
  return s.split("\n").map((part, i) => createElement(Fragment, { key: i }, i > 0 && createElement("br"), part));
}

export function contentList(key, fallback) {
  try {
    const v = overrides[key];
    const list = v ? JSON.parse(v) : null;
    return Array.isArray(list) ? list : fallback;
  } catch {
    return fallback;
  }
}

export const useContent = () => useContext(Ctx).version;
export const useMaintenance = () => useContext(Ctx).maintenance;

const getJson = (path) => fetch(`${BACKEND}/api/${path}?_=${Date.now()}`, { cache: "no-store" }).then((r) => (r.ok ? r.json() : Promise.reject(r.status)));

export function ContentProvider({ children }) {
  const [state, setState] = useState({ version: 0, maintenance: false, ready: false });
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const [c, m] = await Promise.allSettled([getJson("content"), getJson("maintenance")]);
      if (!alive) return;
      if (c.status === "fulfilled") {
        const next = c.value?.overrides || {};
        if (JSON.stringify(next) !== JSON.stringify(overrides)) {
          overrides = next;
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch { /* storage full or blocked */ }
          setState((s) => ({ ...s, version: s.version + 1 }));
        }
      }
      // Status fetch failed: render the site normally.
      const on = m.status === "fulfilled" && !!m.value?.on;
      setState((s) => (s.maintenance === on && s.ready ? s : { ...s, maintenance: on, ready: true }));
    };
    load();
    const cap = setTimeout(() => alive && setState((s) => (s.ready ? s : { ...s, ready: true })), 1200);
    let es;
    try {
      es = new EventSource(`${BACKEND}/api/content/stream`);
      es.addEventListener("version", load);
    } catch { /* no SSE: focus refetch + slow poll still apply */ }
    const id = setInterval(load, 60000);
    const onVis = () => document.visibilityState === "visible" && load();
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("content:refresh", load);
    return () => {
      alive = false; clearTimeout(cap); clearInterval(id); es?.close();
      window.removeEventListener("focus", load); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("content:refresh", load);
    };
  }, []);
  if (!state.ready) return createElement("div", { className: "min-h-[100dvh] bg-slate-950", "data-testid": "content-loading" });
  return createElement(Ctx.Provider, { value: state }, children);
}
